/**
 * src/utils/identityAccessTasks.js
 *
 * Identity module — turning derived access findings into work.
 *
 * IDNA-02 derives three findings per person (grant gap, drift, revocation due)
 * and shows them on the Role Matrix. A finding nobody is shown is a finding
 * nobody acts on, so this turns them into entries in `state.adhocTasks[]` —
 * the ONE task system (CLAUDE.md), the same array User Access Review campaigns
 * already emit `access-revocation` entries into. There is deliberately no
 * bespoke proposal queue.
 *
 * This module is pure: it takes the matrix and the current task list and says
 * what should be created and what should be withdrawn. Applying that is the
 * caller's job, so the decision can be tested without a store.
 *
 * TWO RULES DO THE REAL WORK HERE:
 *
 *  1. IDEMPOTENCE. The sweep re-runs hourly. Every task carries a stable
 *     `identityKey` derived from (person, finding kind, role system, role), and
 *     a finding whose key already has an open task creates nothing. Without
 *     this the task list doubles every hour until nobody reads it.
 *
 *  2. WITHDRAWAL, BUT ONLY ON EVIDENCE. A finding that has been resolved closes
 *     its task — an open "grant X" for access that has since been granted is
 *     exactly the noise that makes people stop reading the list. But a task is
 *     only withdrawn when its finding could actually have been observed to be
 *     gone. When the governance store was unreadable, governance findings were
 *     suppressed rather than disproven (IDN-B-004), so their tasks are LEFT
 *     ALONE. Closing them would silently retire real access work on the
 *     strength of a failed network call.
 */

import { generateId, now } from './formatting.js';
import { closeTask, systemClosure } from './taskClosure.js';
import { GOVERNANCE_STATUS } from './roleMatrix.js';
import { uarCoveredIdentityKeys, holdingKey } from './uarIdentityEvidence.js';

/** Marks the tasks this sweep owns, mirroring the engine's own convention
 *  (`engineGenerated` + `sourceJobId`) used by createTaskIfMissing.js. */
export const IDENTITY_TASK_SOURCE = 'identity-access';

/** Task types, extending the vocabulary UAR campaigns already emit
 *  (`access-revocation`) rather than inventing a parallel one. */
export const IDENTITY_TASK_TYPES = {
  'grant-gap': 'access-grant',
  drift: 'access-drift',
  'revocation-due': 'access-revocation',
};

const DUE_DAYS = { 'grant-gap': 14, drift: 30, 'revocation-due': 7 };
const PRIORITY = { 'grant-gap': 'medium', drift: 'medium', 'revocation-due': 'high' };
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Stable identity of one finding. Two sweeps over unchanged data produce the
 *  same key, which is what makes creation idempotent. */
export function identityTaskKey(person, finding) {
  return [IDENTITY_TASK_SOURCE, finding.kind, finding.system, person.id, finding.id].join('|');
}

const SYSTEM_LABEL = { platform: 'platform role', governance: 'governance role' };

function titleFor(person, finding) {
  const what = `${finding.name} (${SYSTEM_LABEL[finding.system]})`;
  if (finding.kind === 'grant-gap') return `Grant ${what} to ${person.name}`;
  if (finding.kind === 'revocation-due') return `Revoke ${what} from ${person.name}`;
  return `Review ${what} held by ${person.name}`;
}

function descriptionFor(person, finding) {
  const lead =
    finding.kind === 'grant-gap'
      ? `${person.name} holds a job role that requires this access but does not have it, so they cannot do the job the role describes.`
      : finding.kind === 'revocation-due'
        ? `${person.name} still holds this access after the reason for it ended.`
        : `${person.name} holds this access and no job role of theirs requires it.`;
  return [
    lead,
    `Reason: ${finding.because}.`,
    `Account: ${person.email || 'unknown'}.`,
    'Raised by the Role Matrix. Confirming it is a separate, explicit action — this task records the need, not the change.',
  ].join(' ');
}

/**
 * Decide which identity tasks to create and which to withdraw.
 *
 * @param {object} input
 * @param {object} input.matrix          buildRoleMatrix() output
 * @param {Array}  input.adhocTasks      state.adhocTasks
 * @param {string} input.assigneeId      the administrator who owns this work
 * @param {string} input.governanceStatus GOVERNANCE_STATUS value for this sweep
 * @returns {{ toCreate: Array, toCloseIds: string[], unchanged: number }}
 */
export function planIdentityAccessTasks({
  matrix,
  adhocTasks = [],
  assigneeId = null,
  governanceStatus = GOVERNANCE_STATUS.UNAVAILABLE,
}) {
  const governanceTrusted = governanceStatus === GOVERNANCE_STATUS.OK;

  // Every finding the matrix currently reports, by key.
  const currentByKey = new Map();
  for (const person of matrix?.people || []) {
    for (const finding of person.accessFindings || []) {
      currentByKey.set(identityTaskKey(person, finding), { person, finding });
    }
  }

  const ourOpenTasks = (adhocTasks || []).filter(
    (t) => t?.sourceJobId === IDENTITY_TASK_SOURCE && t.identityKey && !isClosed(t)
  );
  const openKeys = new Set(ourOpenTasks.map((t) => t.identityKey));

  // A reviewer who has already called for this access to be revoked in a UAR
  // campaign does not need the sweep asking for it again. The two processes
  // decide at different granularity — UAR per employee, the derivation per
  // access — so the UAR task carries the specific holdings it covers, and this
  // is the one place that reads them (IDNA-05).
  const uarCovered = uarCoveredIdentityKeys(adhocTasks);

  const toCreate = [];
  for (const [key, { person, finding }] of currentByKey) {
    if (openKeys.has(key)) continue;
    if (
      finding.kind === 'revocation-due' &&
      uarCovered.has(`${person.id}|${holdingKey(finding.system, finding.id)}`)
    ) {
      continue;
    }
    toCreate.push(buildTask({ key, person, finding, assigneeId }));
  }

  const toCloseIds = [];
  for (const task of ourOpenTasks) {
    if (currentByKey.has(task.identityKey)) continue;
    // IDN-B-004 — a governance finding that was suppressed because the store
    // could not be read has not been observed to be resolved. Leave its task
    // standing; a failed read must never retire access work.
    if (task.identitySystem === 'governance' && !governanceTrusted) continue;
    toCloseIds.push(task.id);
  }

  return {
    toCreate,
    toCloseIds,
    unchanged: ourOpenTasks.length - toCloseIds.length,
  };
}

function isClosed(task) {
  return task.status === 'closed' || task.status === 'completed';
}

function buildTask({ key, person, finding, assigneeId }) {
  return {
    id: 'task_' + generateId(),
    title: titleFor(person, finding),
    description: descriptionFor(person, finding),
    type: 'automatic',
    taskType: IDENTITY_TASK_TYPES[finding.kind],
    status: 'open',
    priority: PRIORITY[finding.kind],
    assigneeId,
    dueDate: new Date(Date.now() + DUE_DAYS[finding.kind] * MS_PER_DAY).toISOString(),
    createdAt: now(),
    evidence: [],
    controls: [],
    engineGenerated: true,
    sourceJobId: IDENTITY_TASK_SOURCE,
    // Everything a later phase needs to action this without re-deriving it.
    identityKey: key,
    identityKind: finding.kind,
    identitySystem: finding.system,
    identityAccessId: finding.id,
    identityAccessName: finding.name,
    identityReason: finding.because,
    linkedEmployeeId: person.employeeId || null,
    linkedUserId: person.userId || null,
    linkedEntityId: person.employeeId || person.userId || null,
    linkedEntityType: 'employee',
  };
}

/**
 * Apply a plan to a state draft. Kept next to the planner so the two halves
 * cannot drift, and so callers do not hand-roll the closure stamp.
 */
export function applyIdentityAccessTasks(draft, plan) {
  if (!plan) return { created: 0, closed: 0 };
  const list = draft.adhocTasks || (draft.adhocTasks = []);
  const closing = new Set(plan.toCloseIds);

  if (closing.size) {
    draft.adhocTasks = list.map((t) =>
      closing.has(t.id)
        ? // Says why on the record: the need went away, nobody did the work.
          // A system closure record (GH #7704), naming the sweep that closed it.
          closeTask(
            t,
            systemClosure({
              reason: 'Withdrawn — the Role Matrix no longer reports this finding.',
              trigger: 'identity-access-sweep',
              at: now(),
            })
          )
        : t
    );
  }
  if (plan.toCreate.length) {
    draft.adhocTasks = [...draft.adhocTasks, ...plan.toCreate];
  }
  return { created: plan.toCreate.length, closed: closing.size };
}
